<template>
  <rs-side-panel ref="sidePanel">
    <template slot="header">{{ panelTitle }}</template>
    <template slot="body">
      <v-form ref="form" @submit.prevent="handleFormSubmit">
        <v-row dense>
          <v-col cols="12" sm="6">
            <rs-select
              :items="selectablePaymentProviderList"
              :rules="[rules.required]"
              label="Ödeme Tedarikçisi"
              v-model="formData.payment_provider_id"
              item-text="name"
              item-value="id"
              :readonly="providerSelectionReadonly"
              :filled="providerSelectionReadonly"
              @change="handlePaymentProviderChange"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <rs-select-bank-account
              label="POS Hesabı"
              v-model="formData.pos_account_id"
              :rules="[rules.required]"
              hint="POS tahsilatları bu banka hesabına yapılır"
              persistent-hint
              :readonly="isExistingProviderMode"
              :filled="isExistingProviderMode"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <rs-select-bank-account
              label="Banka Hesabı"
              v-model="formData.bank_account_id"
              :rules="[
                rules.required,
                formData.pos_account_id !== formData.bank_account_id ||
                  'POS hesabıyla aynı olmamalı',
              ]"
              hint="POS tahsilatları bu hesaba topluca aktarılır"
              persistent-hint
              @change="handleBankAccountChange"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-switch
              v-model="formData.is_active"
              :disabled="disabled || isExistingProviderMode"
              :label="
                formData.is_active
                  ? 'Bu TYA için seçili sağlayıcı'
                  : 'Pasif ekle'
              "
              hide-details="auto"
            ></v-switch>
          </v-col>

          <v-col sm="6" v-if="formData.payment_provider_id === 1">
            <rs-text-field
              label="Sipay Alt Üye İşyeri ID"
              v-model="formData.submerchant_id"
              :rules="[
                rules.required,
                rules.minLength(formData.submerchant_id, 10),
                rules.maxLength(formData.submerchant_id, 100),
              ]"
              required
            />
          </v-col>

          <v-col
            cols="12"
            v-if="
              selectedProviderOption &&
              selectedProviderOption.adapter_supported !== undefined
            "
          >
            <v-alert
              dense
              text
              :type="
                selectedProviderOption.provider_enabled ? 'info' : 'warning'
              "
              class="mb-0"
            >
              {{
                selectedProviderOption.provider_enabled
                  ? "Bu sağlayıcının adapter'ı sistemde aktif."
                  : "Bu sağlayıcının adapter'ı kayıtlı; global config kapalı olabilir."
              }}
            </v-alert>
          </v-col>

          <v-col
            cols="12"
            v-if="
              formData.payment_provider_id === 2 &&
              requiresProviderProvisioningFields
            "
          >
            <v-divider class="my-6" />

            <v-row dense>
              <v-col cols="12"><h3>Param Detayları</h3></v-col>

              <v-col cols="12" sm="6">
                <rs-select
                  v-model="formDataParam.company_type_id"
                  :disabled="disabled"
                  label="Şirket Türü"
                  :rules="[rules.required]"
                  :items="[
                    {
                      id: 1,
                      name: 'Kişi',
                    },
                    {
                      id: 2,
                      name: 'Şahıs Şirketi',
                    },
                    {
                      id: 3,
                      name: 'Limited veya Anonim Şirket',
                    },
                  ]"
                  hide-details="auto"
                ></rs-select>
              </v-col>

              <v-col cols="12" sm="6">
                <rs-tel-field
                  label="Cep Telefonu"
                  :rules="[rules.required, rules.numbersOnlyWithParam]"
                  v-model="formDataParam.mobile_phone"
                  :disabled="disabled"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <rs-text-field
                  label="Yetkili Kişi"
                  v-model="formDataParam.person_name"
                  :rules="[
                    rules.required,
                    rules.maxLength(formDataParam.person_name, 100),
                  ]"
                  :readonly="disabled"
                  :filled="disabled"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <rs-datepicker
                  label="Yetkili kişi doğum tarihi"
                  v-model="formDataParam.birthdate"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <v-col cols="12" sm="6">
                <rs-text-field
                  class="identity-number"
                  :label="$t('labels.identity_no')"
                  v-model="formDataParam.tc_number"
                  :rules="[
                    rules.exactLength(formDataParam.tc_number, 11),
                    rules.numbersOnly,
                    rules.required,
                  ]"
                  minlength="11"
                  maxlength="11"
                  :readonly="disabled"
                  :filled="disabled"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <rs-text-field
                  label="IBAN hesap sahibi"
                  v-model="formDataParam.iban_title"
                  readonly
                  hint="Banka hesap tanımlarındaki kayıtlı hesap adı"
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col
            cols="12"
            v-if="
              formData.payment_provider_id === 3 && showIyzicoProvisioningFields
            "
          >
            <v-divider class="my-6" />

            <v-row dense>
              <v-col cols="12"><h3>Iyzico Detayları</h3></v-col>

              <v-col
                cols="12"
                v-if="
                  isIyzicoSubmerchantPreviewLoading ||
                  iyzicoSubmerchantPreview ||
                  iyzicoSubmerchantPreviewError
                "
              >
                <v-alert
                  dense
                  text
                  :type="iyzicoSubmerchantPreviewAlertType"
                  class="mb-0"
                >
                  {{ iyzicoSubmerchantPreviewMessage }}
                </v-alert>
              </v-col>

              <v-col cols="12" sm="6">
                <rs-select
                  v-model="formDataIyzico.sub_merchant_type"
                  :disabled="disabled"
                  label="Alt Üye İşyeri Tipi"
                  :rules="[rules.required]"
                  :items="[
                    { id: 'PERSONAL', name: 'Kişi' },
                    { id: 'PRIVATE_COMPANY', name: 'Şahıs Şirketi' },
                    {
                      id: 'LIMITED_OR_JOINT_STOCK_COMPANY',
                      name: 'Limited veya Anonim Şirket',
                    },
                  ]"
                  hide-details="auto"
                  @change="handleIyzicoSubmerchantTypeChange"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <rs-text-field
                  label="Alt Üye Dış Referans"
                  v-model="formDataIyzico.sub_merchant_external_id"
                  readonly
                  filled
                  hint="TYA bazlı otomatik oluşturulur"
                  persistent-hint
                />
              </v-col>

              <v-col cols="12" sm="6">
                <rs-text-field
                  label="E-posta"
                  v-model="formDataIyzico.email"
                  :rules="[rules.required, rules.email]"
                  :readonly="disabled"
                  :filled="disabled"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <rs-tel-field
                  label="Cep Telefonu"
                  :rules="[rules.required, rules.numbersOnlyWithParam]"
                  v-model="formDataIyzico.gsm_number"
                  :disabled="disabled"
                />
              </v-col>

              <v-col cols="12" sm="6" v-if="isIyzicoPersonal">
                <rs-text-field
                  label="Yetkili Adı"
                  v-model="formDataIyzico.contact_name"
                  :rules="[rules.required]"
                  :readonly="disabled"
                  :filled="disabled"
                />
              </v-col>

              <v-col cols="12" sm="6" v-if="isIyzicoPersonal">
                <rs-text-field
                  label="Yetkili Soyadı"
                  v-model="formDataIyzico.contact_surname"
                  :rules="[rules.required]"
                  :readonly="disabled"
                  :filled="disabled"
                />
              </v-col>

              <v-col cols="12" sm="6" v-if="isIyzicoPersonal">
                <rs-text-field
                  class="identity-number"
                  :label="$t('labels.identity_no')"
                  v-model="formDataIyzico.identity_number"
                  :rules="iyzicoIdentityNumberRules"
                  minlength="11"
                  maxlength="11"
                  :readonly="disabled"
                  :filled="disabled"
                  :hint="
                    isEditMode
                      ? 'Mevcut değer Iyzico tarafından döndürülmez; değiştirmek için yeniden girin'
                      : null
                  "
                  :persistent-hint="isEditMode"
                />
              </v-col>

              <v-col cols="12" sm="6" v-if="isIyzicoCompany">
                <rs-text-field
                  label="Ticari Ünvan"
                  v-model="formDataIyzico.legal_company_title"
                  :rules="[
                    rules.required,
                    rules.maxLength(formDataIyzico.legal_company_title, 255),
                  ]"
                  :readonly="disabled"
                  :filled="disabled"
                />
              </v-col>

              <v-col cols="12" sm="6" v-if="isIyzicoCompany">
                <rs-text-field
                  label="Vergi Dairesi"
                  v-model="formDataIyzico.tax_office"
                  :rules="[
                    rules.required,
                    rules.maxLength(formDataIyzico.tax_office, 100),
                  ]"
                  :readonly="disabled"
                  :filled="disabled"
                />
              </v-col>

              <v-col cols="12" sm="6" v-if="isIyzicoCapitalCompany">
                <rs-text-field
                  label="Vergi Numarası"
                  v-model="formDataIyzico.tax_number"
                  :rules="[
                    rules.exactLength(formDataIyzico.tax_number, 10),
                    rules.numbersOnly,
                    rules.required,
                  ]"
                  minlength="10"
                  maxlength="10"
                  :readonly="disabled"
                  :filled="disabled"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <rs-form-buttons
          hide-submit
          :is-loading="isLoading"
          @cancel="hide()"
          @submit="handleFormSubmit"
        />
      </v-form>
    </template>
  </rs-side-panel>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { hasForm, hasPermissions } from "@/view/mixins";

export default {
  name: "ClusterPaymentProviderForm",
  mixins: [hasForm, hasPermissions],
  props: {
    house: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters(["clusterId", "paymentProviderList"]),
    selectablePaymentProviderList() {
      const list = this.paymentProviderOptions.length
        ? this.paymentProviderOptions
        : this.paymentProviderList;

      return list.filter((item) => item.adapter_supported !== false);
    },
    selectedProviderOption() {
      return this.selectablePaymentProviderList.find(
        (item) => item.id === this.formData.payment_provider_id
      );
    },
    isProvisionMode() {
      return this.mode === "provision";
    },
    isEditMode() {
      return this.mode === "edit";
    },
    isExistingProviderMode() {
      return this.isProvisionMode || this.isEditMode;
    },
    panelTitle() {
      if (this.isEditMode) {
        return "Iyzico Alt Üye İşyeri Düzenle";
      }

      return this.isProvisionMode
        ? "Ödeme Sağlayıcı Hazırlığı"
        : "Ödeme Tedarikçisi Ekle";
    },
    providerSelectionReadonly() {
      return this.disabled || this.isExistingProviderMode;
    },
    requiresProviderProvisioningFields() {
      return !this.formData.submerchant_id;
    },
    showIyzicoProvisioningFields() {
      return this.requiresProviderProvisioningFields || this.isEditMode;
    },
    iyzicoIdentityNumberRules() {
      const rules = [
        this.rules.exactLength(this.formDataIyzico.identity_number, 11),
        this.rules.numbersOnly,
      ];

      if (!this.isEditMode) {
        rules.push(this.rules.required);
      }

      return rules;
    },
    isIyzicoPersonal() {
      return this.formDataIyzico.sub_merchant_type === "PERSONAL";
    },
    isIyzicoPrivateCompany() {
      return this.formDataIyzico.sub_merchant_type === "PRIVATE_COMPANY";
    },
    isIyzicoCapitalCompany() {
      return (
        this.formDataIyzico.sub_merchant_type ===
        "LIMITED_OR_JOINT_STOCK_COMPANY"
      );
    },
    isIyzicoCompany() {
      return this.isIyzicoPrivateCompany || this.isIyzicoCapitalCompany;
    },
    iyzicoSubmerchantPreviewAlertType() {
      if (this.iyzicoSubmerchantPreviewError) {
        return "warning";
      }

      if (this.isIyzicoSubmerchantPreviewLoading) {
        return "info";
      }

      if (this.isEditMode && this.iyzicoSubmerchantPreview?.exists === false) {
        return "warning";
      }

      return this.iyzicoSubmerchantPreview?.exists ? "success" : "info";
    },
    iyzicoSubmerchantPreviewMessage() {
      if (this.iyzicoSubmerchantPreviewError) {
        return this.iyzicoSubmerchantPreviewError;
      }

      if (this.isIyzicoSubmerchantPreviewLoading) {
        return "Iyzico alt üye işyeri kaydı kontrol ediliyor.";
      }

      if (this.isEditMode) {
        return this.iyzicoSubmerchantPreview?.exists
          ? "Iyzico tarafındaki güncel alt üye işyeri bilgileri yüklendi."
          : "Iyzico tarafında güncellenecek alt üye işyeri kaydı bulunamadı.";
      }

      if (this.iyzicoSubmerchantPreview?.exists) {
        return "Iyzico tarafında bu TYA için mevcut alt üye işyeri bulundu. Kaydettiğinizde mevcut kayıt Robosay'a bağlanacak.";
      }

      return "Iyzico tarafında bu TYA için mevcut alt üye işyeri bulunamadı. Kaydettiğinizde yeni kayıt oluşturulacak.";
    },
  },
  data() {
    return {
      formData: this.getEmptyFormData(),
      formDataParam: this.getEmptyParamFormData(),
      formDataIyzico: this.getEmptyIyzicoFormData(),
      isIyzicoSubmerchantPreviewLoading: false,
      iyzicoSubmerchantPreview: null,
      iyzicoSubmerchantPreviewError: null,
      iyzicoSubmerchantPreviewRequestId: 0,
      paymentProviderOptions: [],
      id: null,
      mode: "create",
      disabled: false,
      extraParams: {},
    };
  },
  methods: {
    ...mapActions(["updateClusterId"]),
    show(id, disabled, extraParams) {
      this.mode = "create";
      this.id = id || null;
      this.disabled = Boolean(disabled);
      this.formData = Object.assign(this.getEmptyFormData(), extraParams || {});
      this.formDataParam = this.getEmptyParamFormData();
      this.formDataIyzico = this.getEmptyIyzicoFormData();
      this.resetIyzicoSubmerchantPreview();

      this.originalData = {};
      this.extraParams = {};

      this.$refs.sidePanel.show();
      this.loadPaymentProviderOptions();
      this.$nextTick(() => this.handlePaymentProviderChange());
    },
    showProvision(item) {
      this.showExistingProvider(item, "provision");
    },
    showEdit(item) {
      this.showExistingProvider(item, "edit");
    },
    showExistingProvider(item, mode) {
      this.mode = mode;
      this.id = item.id;
      this.disabled = false;
      this.extraParams = {};
      this.formData = Object.assign(this.getEmptyFormData(), {
        payment_provider_id: item.payment_provider_id,
        pos_account_id: item.pos_account_id,
        bank_account_id: item.bank_account_id,
        is_active: item.is_active,
        submerchant_id: item.submerchant_id,
      });
      this.formDataParam = this.getEmptyParamFormData();
      this.formDataIyzico = Object.assign(
        this.getEmptyIyzicoFormData(),
        item.provider_metadata?.submerchant_profile_snapshot || {},
        {
          sub_merchant_external_id: this.getIyzicoExternalId(item),
        }
      );
      this.resetIyzicoSubmerchantPreview();
      this.originalData = {};

      this.$refs.sidePanel.show();
      this.loadPaymentProviderOptions();
      this.$nextTick(() => this.handlePaymentProviderChange());
    },
    getIyzicoExternalId(item) {
      return (
        item.provider_metadata?.submerchant_profile_snapshot
          ?.sub_merchant_external_id ||
        item.external_account_id ||
        `cluster-${this.clusterId}`
      );
    },
    hide() {
      this.$refs.sidePanel.hide();
    },
    handleCancelClick() {
      this.hide();
    },
    getEmptyFormData() {
      return {
        payment_provider_id: 2,
        pos_account_id: null,
        bank_account_id: null,
        is_active: true,
        submerchant_id: null,
      };
    },
    getEmptyParamFormData() {
      return {
        company_type_id: null,
        mobile_phone: null,
        person_name: null,
        birthdate: null,
        tc_number: null,
        iban_title: null,
      };
    },
    getEmptyIyzicoFormData() {
      return {
        email: null,
        gsm_number: null,
        contact_name: null,
        contact_surname: null,
        identity_number: null,
        tax_office: null,
        tax_number: null,
        legal_company_title: null,
        sub_merchant_external_id: null,
        sub_merchant_type: "PERSONAL",
      };
    },
    handleFormSubmit() {
      if (this.isLoading || !this.$refs.form.validate()) {
        this.$toast.error("Lütfen tüm gerekli alanları doldurunuz");
        return false;
      }

      const formData = Object.assign({}, this.formData);
      const params = Object.assign({}, this.extraParams, formData);
      params.cluster_id = this.clusterId;

      if (params.payment_provider_id === 2) {
        params.param = Object.assign({}, this.formDataParam);
        params.param.mobile_phone = (params.param.mobile_phone || "").replace(
          /[^0-9+]/g,
          ""
        );
      }

      if (params.payment_provider_id === 3) {
        params.iyzico = Object.assign({}, this.formDataIyzico);
        params.iyzico.gsm_number = (params.iyzico.gsm_number || "").replace(
          /[^0-9+]/g,
          ""
        );
      }

      this.isLoading = true;

      const request = this.isExistingProviderMode
        ? this.$api.post(
            `cluster-payment-providers/${this.id}/provision`,
            params
          )
        : this.$api.post("cluster-payment-providers", params);

      request
        .then((response) => {
          this.$toast.success(this.$t("saved"));
          this.$emit("created", response.data.data);
          this.$emit("saved", response.data.data);
          this.$nextTick(() => this.hide());
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    loadPaymentProviderOptions() {
      this.$api
        .query("cluster-payment-providers/providers")
        .then((response) => {
          this.paymentProviderOptions = response.data.data || [];
        })
        .catch(() => {
          this.paymentProviderOptions = [];
        });
    },
    handlePaymentProviderChange() {
      this.resetIyzicoSubmerchantPreview();

      if (
        this.formData.payment_provider_id === 3 &&
        !this.formDataIyzico.sub_merchant_external_id
      ) {
        this.formDataIyzico.sub_merchant_external_id = `cluster-${this.clusterId}`;
      }

      this.previewIyzicoSubmerchant();
    },
    handleIyzicoSubmerchantTypeChange() {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },
    resetIyzicoSubmerchantPreview() {
      this.isIyzicoSubmerchantPreviewLoading = false;
      this.iyzicoSubmerchantPreview = null;
      this.iyzicoSubmerchantPreviewError = null;
      this.iyzicoSubmerchantPreviewRequestId += 1;
    },
    previewIyzicoSubmerchant() {
      if (
        this.disabled ||
        this.formData.payment_provider_id !== 3 ||
        !this.showIyzicoProvisioningFields ||
        !this.clusterId
      ) {
        return;
      }

      if (!this.formDataIyzico.sub_merchant_external_id) {
        this.formDataIyzico.sub_merchant_external_id = `cluster-${this.clusterId}`;
      }

      const requestId = this.iyzicoSubmerchantPreviewRequestId + 1;
      this.iyzicoSubmerchantPreviewRequestId = requestId;
      this.isIyzicoSubmerchantPreviewLoading = true;
      this.iyzicoSubmerchantPreview = null;
      this.iyzicoSubmerchantPreviewError = null;

      this.$api
        .post("cluster-payment-providers/submerchant-preview", {
          cluster_id: this.clusterId,
          payment_provider_id: this.formData.payment_provider_id,
          iyzico: {
            sub_merchant_external_id:
              this.formDataIyzico.sub_merchant_external_id,
          },
        })
        .then((response) => {
          if (requestId !== this.iyzicoSubmerchantPreviewRequestId) {
            return;
          }

          const preview = response.data.data || {};
          this.iyzicoSubmerchantPreview = preview;
          this.applyIyzicoSubmerchantPreview(preview);
        })
        .catch((error) => {
          if (requestId !== this.iyzicoSubmerchantPreviewRequestId) {
            return;
          }

          this.iyzicoSubmerchantPreviewError = this.previewErrorMessage(error);
        })
        .finally(() => {
          if (requestId !== this.iyzicoSubmerchantPreviewRequestId) {
            return;
          }

          this.isIyzicoSubmerchantPreviewLoading = false;
        });
    },
    applyIyzicoSubmerchantPreview(preview) {
      if (!preview || !preview.exists || !preview.profile) {
        return;
      }

      [
        "email",
        "gsm_number",
        "contact_name",
        "contact_surname",
        "identity_number",
        "tax_office",
        "tax_number",
        "legal_company_title",
        "sub_merchant_external_id",
        "sub_merchant_type",
      ].forEach((key) => {
        if (
          preview.profile[key] &&
          (this.isEditMode ||
            this.formDataIyzico[key] === null ||
            this.formDataIyzico[key] === "")
        ) {
          this.formDataIyzico[key] = preview.profile[key];
        }
      });
    },
    previewErrorMessage(error) {
      const errors = error?.response?.data?.errors;

      if (Array.isArray(errors) && errors.length) {
        const firstError = Array.isArray(errors[0]) ? errors[0][0] : errors[0];

        if (firstError) {
          return firstError;
        }
      }

      return (
        error?.response?.data?.message ||
        "Iyzico mevcut alt üye işyeri kontrolü yapılamadı. Kaydettiğinizde sistem tekrar kontrol edecek."
      );
    },
    handleBankAccountChange(bankAccountId) {
      this.isLoading = true;

      this.$api
        .get(`bank-accounts`, bankAccountId)
        .then((response) => {
          this.formDataParam.iban_title = response.data.data.title;
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
